import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "@/pages/auth/components/AuthLayout";
import { supabase } from "@/lib/supabase";

function getPasswordStrength(password: string): {
  score: number;
  label: string;
  color: string;
  barColor: string;
} {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { score, label: "Weak", color: "text-red-500", barColor: "bg-red-400" };
  if (score === 2) return { score, label: "Fair", color: "text-amber-500", barColor: "bg-amber-400" };
  if (score === 3) return { score, label: "Good", color: "text-yellow-500", barColor: "bg-yellow-400" };
  if (score === 4) return { score, label: "Strong", color: "text-[#16a34a]", barColor: "bg-[#16a34a]" };
  return { score, label: "Very Strong", color: "text-[#16a34a]", barColor: "bg-[#16a34a]" };
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  const strength = getPasswordStrength(password);

  useEffect(() => {
    // Supabase handles the token from the URL hash automatically
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setSessionReady(true);
      }
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    const { error: updateError } = await supabase.auth.updateUser({ password });

    setLoading(false);

    if (updateError) {
      setError(updateError.message || "Failed to update password. The link may have expired.");
      return;
    }

    setSuccess(true);
    setTimeout(() => navigate("/login"), 3000);
  };

  if (!sessionReady) {
    return (
      <AuthLayout
        title="Reset Your Password"
        subtitle="Verifying your reset link..."
      >
        <div className="flex flex-col items-center justify-center py-8 gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-[#16a34a] animate-spin"></div>
          <p className="text-sm text-gray-500">Validating your reset link...</p>
          <p className="text-xs text-gray-400">
            If this takes too long,{" "}
            <a href="/forgot-password" className="text-[#16a34a] hover:underline">
              request a new link
            </a>
            .
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set New Password"
      subtitle="Choose a strong password for your account."
    >
      {success ? (
        <div className="space-y-6 text-center py-2">
          <div className="w-16 h-16 bg-[#f0fdf4] rounded-full flex items-center justify-center mx-auto">
            <i className="ri-shield-check-line text-[#16a34a] text-3xl"></i>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Password updated!</h3>
            <p className="text-gray-500 text-sm">
              Your password has been changed successfully. Redirecting you to sign in...
            </p>
          </div>
          <div className="flex justify-center">
            <div className="w-6 h-6 rounded-full border-2 border-gray-200 border-t-[#16a34a] animate-spin"></div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg flex items-center gap-2">
              <i className="ri-error-warning-line flex-shrink-0"></i>
              {error}
            </div>
          )}

          {/* New password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                placeholder="Min. 8 characters"
                autoFocus
                className="w-full px-4 py-2.5 pr-11 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
              </button>
            </div>

            {/* Strength meter */}
            {password.length > 0 && (
              <div className="mt-2 space-y-1.5">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                        i <= strength.score ? strength.barColor : "bg-gray-200"
                      }`}
                    />
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium ${strength.color}`}>
                    {strength.label}
                  </span>
                  <span className="text-xs text-gray-400">
                    {strength.score < 3 ? "Add uppercase, numbers & symbols" : "Looking good!"}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Confirm password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                placeholder="Repeat your password"
                className={`w-full px-4 py-2.5 pr-11 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition ${
                  confirmPassword.length > 0 && confirmPassword !== password
                    ? "border-red-300 bg-red-50"
                    : confirmPassword.length > 0 && confirmPassword === password
                    ? "border-green-400 bg-green-50/30"
                    : "border-gray-200 bg-gray-50"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className={showConfirm ? "ri-eye-off-line" : "ri-eye-line"}></i>
              </button>
            </div>
            {confirmPassword.length > 0 && confirmPassword !== password && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <i className="ri-close-circle-line"></i> Passwords do not match
              </p>
            )}
            {confirmPassword.length > 0 && confirmPassword === password && (
              <p className="text-[#16a34a] text-xs mt-1 flex items-center gap-1">
                <i className="ri-checkbox-circle-line"></i> Passwords match
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || password !== confirmPassword || password.length < 8}
            className="w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Updating password...
              </span>
            ) : (
              <>
                <i className="ri-lock-password-line mr-2"></i>
                Update Password
              </>
            )}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}